import { ApprovalableTypeEnum } from 'api-hooks/common';
import {
  getProjectKey,
  getProjectsKey,
  ProjectModel,
  SaleProjectEnum,
  useRemoveInvoice,
} from 'api-hooks/projects';
import { SaleLiteModel } from 'api-hooks/sale/common';
import { Option } from 'baseui/select';
import formSetErrors from 'common/helpers/form-setError';
import { queryClient } from 'common/repositories/query-client';
import DeleteButton from 'components/common/delete-button';
import DrawerHeader from 'components/common/drawer/drawer-header';
import { DrawerBody } from 'components/common/drawer/styles';
import notification from 'components/common/notification';
import Separator from 'components/common/separator';
import { Form, Input } from 'components/elements';
import { Button } from 'components/elements/buttons';
import {
  FullContainer,
  HalfContainer,
  SeparatorHorizontal,
} from 'components/styles';
import useDialog from 'hooks/use-dialog';
import RequestApprovalButton from 'legacy-modules/approval/approval/components/request-approval-button';
import RequestApprovalModal from 'legacy-modules/approval/approval/components/request-modal';
import CustomerSelectInput from 'legacy-modules/master/customer/components/select-input';
import { CardSection, RowBetweenStyles } from 'legacy-modules/styles';
import useTranslation from 'next-translate/useTranslation';
import React from 'react';
import { useForm } from 'react-hook-form';

type ItemFormType = {
  saleRefId: string | null;
  date: Date;
  code: string;
  description: string;
  customerId: string;
};

interface Props {
  onClose: () => void;
  project?: ProjectModel;
  options?: Option;
  saleData?: SaleLiteModel;
}

export default function RemoveInvoiceFormPanel(props: Props) {
  const { t } = useTranslation();
  const { saleData } = props;

  const { mutateAsync, isLoading } = useRemoveInvoice();

  const dialog = useDialog();

  const defaultValues = React.useMemo<ItemFormType>(() => {
    return {
      saleRefId: saleData?.id! || '',
      code: saleData?.code || '',
      customerId: saleData?.customer?.id || '',
      date: saleData?.transactionAt || new Date(),
      description: saleData?.description || '',
    };
  }, [saleData]);

  const methods = useForm<any>({ defaultValues, mode: 'onChange' });
  const [saleId, setSaleId] = React.useState<any>();
  const onDelete = React.useCallback(async () => {
    try {
      const result = await mutateAsync({
        projectId: props.project?.id!,
        saleRefId: saleId!,
      });
      notification.success({ message: result?.message });

      queryClient.refetchQueries([getProjectsKey()[0]]);

      props.onClose();
      dialog.showCustom({
        render: (onClose) => (
          <RequestApprovalModal
            onClose={onClose}
            data={{
              approvalableId: saleId,
              approvalableType: ApprovalableTypeEnum.Sale,
              workflow: 'sale_project',
            }}
          />
        ),
      });
    } catch (e) {
      if (e.errors) {
        formSetErrors(e.errors, methods.setError);
      }
      notification.error({ message: e.message });
    }
  }, [dialog, methods.setError, mutateAsync, props, saleId]);

  const onDismissApproval = () => {
    queryClient.refetchQueries([getProjectsKey()[0]]);
    queryClient.refetchQueries(getProjectKey({ id: props.project?.id! }));
    props.onClose();
  };
  return (
    <>
      <DrawerHeader
        onClose={props.onClose}
        title={t('common:remove_invoice')}
      />
      <DrawerBody>
        <Form onSubmit={() => {}} methods={methods}>
          <CardSection>
            <FullContainer>
              <Input
                type="select"
                label={t('common:invoice_no')}
                name="saleRefId"
                options={props.options}
                onAfterChange={(params) => {
                  const extraValue = params.value[0].extra;
                  setSaleId(params.value[0].id);
                  methods.setValue('date', extraValue.transactionAt);
                  methods.setValue('code', extraValue.code);
                  methods.setValue('description', extraValue.description);
                  methods.setValue('customerId', extraValue.customer.id);
                }}
                disabled={!!saleData}
              />
            </FullContainer>
            <FullContainer $style={RowBetweenStyles}>
              <HalfContainer>
                <Input
                  name="date"
                  type="date-time"
                  label={t('common:date')}
                  disabled
                />
              </HalfContainer>
              <HalfContainer>
                <Input
                  name="code"
                  type="text"
                  label={t('common:code')}
                  disabled
                />
              </HalfContainer>
            </FullContainer>
            <FullContainer $style={RowBetweenStyles}>
              <HalfContainer>
                <Input
                  name="description"
                  type="text-area"
                  label={t('common:description')}
                  disabled
                />
              </HalfContainer>
              <HalfContainer>
                <CustomerSelectInput name="customerId" disabled />
              </HalfContainer>
            </FullContainer>
          </CardSection>
          <Separator gap={16} />
          <FullContainer style={{ display: 'flex', justifyContent: 'end' }}>
            <SeparatorHorizontal $margin={8} />
            {saleData?.projectStatus === SaleProjectEnum.draft_removal ? (
              <RequestApprovalButton
                data={{
                  approvalableId: saleData.id,
                  approvalableType: ApprovalableTypeEnum.Sale,
                  workflow: 'sale_project',
                }}
                onDismiss={onDismissApproval}
              />
            ) : (
              <>
                <Button buttonType="tertiary" onClick={props.onClose}>
                  {t('common:cancel')}
                </Button>
                <DeleteButton isLoading={isLoading} onClick={onDelete} />
              </>
            )}
          </FullContainer>
        </Form>
      </DrawerBody>
    </>
  );
}
